import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface Notification {
  id: string;
  message: string;
  created_at: string;
  is_from_admin: boolean;
  is_read: boolean;
  user_id: string | null;
  client_id: number | null;
  admin_id: string | null;
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { user, isAdmin } = useAuth();

  const fetchNotifications = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      console.log('Fetching notifications...'); 
      console.log('Current user:', user?.id);
      console.log('Is admin:', isAdmin);

      let query = supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: true });

      if (isAdmin) {
        // For admin users, show messages where:
        // 1. Messages where admin_id matches (messages sent by this admin)
        // 2. Messages where is_from_admin is false (messages from users to admin)
        query = query.or(`admin_id.eq.${user.id},and(is_from_admin.eq.false,admin_id.is.null)`);
      } else {
        // For regular users, show messages where:
        // 1. Messages they sent (user_id matches and is_from_admin is false)
        // 2. Messages sent to them from admin (is_from_admin is true)
        query = query.or(`and(user_id.eq.${user.id},is_from_admin.eq.false),and(is_from_admin.eq.true,user_id.eq.${user.id})`);
      }

      const { data, error } = await query;

      if (error) throw error;

      console.log('Fetched notifications:', data);
      setNotifications(data || []);
    } catch (error) {
      console.error('Error in fetchNotifications:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch notifications"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Set up real-time subscription
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications'
        },
        (payload) => {
          console.log('Real-time notification update:', payload);
          fetchNotifications();
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    // Initial fetch
    fetchNotifications();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, isAdmin]);

  return {
    notifications,
    isLoading,
    refetchNotifications: fetchNotifications
  };
};